import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../api/client';
import { setCurrentUser } from '../hooks/useAuth';
import SocialLoginButton from '../components/SocialLoginButton';
import './Auth.css';

interface LocationState {
  message?: string;
  email?: string;
}

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  
  const [formData, setFormData] = useState({
    login: state?.email || '', // Can be username or email
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(state?.message || null);

  useEffect(() => {
    // Clear success message after 5 seconds
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.login.trim()) {
      setError('Username or email is required');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Use the backend authentication endpoint
      const response = await authApi.signin({
        email: formData.login, // Backend accepts both email and username
        password: formData.password
      });

      const { user, token } = response.data;

      // Store user info in localStorage (in production, use secure HTTP-only cookies)
      setCurrentUser({
        id: user.id,
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.displayName,
        avatar: user.avatar,
        role: user.role,
        status: user.status,
        isAuthenticated: true,
        loginTime: new Date().toISOString(),
        token
      });

      // Redirect to dashboard
      navigate('/', { replace: true });

    } catch (err: any) {
      console.error('Sign in error:', err);
      if (err.response?.status === 401) {
        setError(err.response.data?.error || 'Invalid username/email or password');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider: 'google' | 'github' | 'apple') => {
    // Redirect to the backend OAuth endpoint
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    window.location.href = `${backendUrl}/auth/${provider}`;
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Welcome Back</h1>
          <p>Sign in to your account</p>
        </div>

        {successMessage && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            {successMessage}
          </div>
        )}

        {/* Social Login Buttons */}
        <div className="social-login-section">
          <SocialLoginButton
            provider="google"
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
          />
          <SocialLoginButton
            provider="github"
            onClick={() => handleSocialLogin('github')}
            disabled={loading}
          />
          <SocialLoginButton
            provider="apple"
            onClick={() => handleSocialLogin('apple')}
            disabled={loading}
          />
        </div>

        <div className="auth-divider">
          <span>or sign in with email</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="login">Username or Email *</label>
            <input
              type="text"
              id="login"
              name="login"
              value={formData.login}
              onChange={handleInputChange}
              placeholder="Enter username or email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
            />
          </div>

          <div className="form-options">
            <label className="checkbox-label">
              <input type="checkbox" />
              <span className="checkmark"></span>
              Remember me
            </label>
            <Link to="/forgot-password" className="auth-link">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="demo-accounts">
          <h3>Demo Accounts</h3>
          <p>Use these accounts to test the application:</p>
          <div className="demo-account-list">
            <div className="demo-account">
              <strong>Admin:</strong> brian / any password
            </div>
            <div className="demo-account">
              <strong>User:</strong> demo / any password
            </div>
          </div>
        </div>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/signup" className="auth-link">
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;